import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../auth/api";
import { alertSuccess, alertError } from "../components/Alert";


export const fetchMessageBox = createAsyncThunk(
  "smsBox/fetchMessageBox",
  async ({ page = 1, search = "", fetchAll = false } = {}, { rejectWithValue, getState }) => {
    try {
      const { smsBox } = getState();
      const { pageSize, cache } = smsBox;

      // ✅ SAME cache key pattern
      const cacheKey = `${page}_${search}_${pageSize}_${fetchAll}`;

      // ✅ Cache check
      if (cache[cacheKey]) {
        return {
          data: cache[cacheKey],
          page,
          search,
          pageSize,
          cacheKey,
          fromCache: true,
        };
      }

      let url = `/surveyHistory/panel/messageBox`;

      if (!fetchAll) {
        url += `?page=${page}&page_size=${pageSize}`;
      } else {
        url += `?page=1&page_size=10000`;
      }

      if (search) {
        url += `&search=${search}`;
      }

      const response = await api.get(url);

      return {
        data: response.data,
        page,
        search,
        pageSize,
        cacheKey,
        fromCache: false,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


// Add Message to the Box
export const addMessageBox = createAsyncThunk(
  "messageBox/addMessageBox",
  async (newMessageData, { rejectWithValue }) => {
    try {
      const response = await api.post("/surveyHistory/panel/messageBox", newMessageData);
      alertSuccess(response.data.success);
      return response.data;
    } catch (error) {
      alertError(error.response?.data.errors);
      return rejectWithValue(error.response?.data.errors || error.message);
    }
  }
);

// Update Message Box Data
export const updateMessageBox = createAsyncThunk(
  "messageBox/updateMessageBox",
  async (messageData, { rejectWithValue }) => {
    try {
      const response = await api.patch("/surveyHistory/panel/messageBox", messageData);
      alertSuccess(response.data.success);
      return response.data;
    } catch (error) {
      alertError(error.response?.data.errors);
      return rejectWithValue(error.response?.data.errors || error.message);
    }
  }
);

// Message Box Slice
const messageBoxSlice = createSlice({
  name: "messageBox",
  initialState: {
    messageBoxList: { result: [], pagination: { count: 0, no_page: 1 } },
     cache: {}, // 🔥 ADD THIS
    search: "",
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    status: "idle",
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.currentPage = 1; // reset page
    },
    setSearch: (state, action) => {
      state.search = action.payload;
      state.currentPage = 1; // reset page
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Message Box Data
      .addCase(fetchMessageBox.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      // .addCase(fetchMessageBox.fulfilled, (state, action) => {
      //   state.status = "succeeded";
      //   state.messageBoxList = action.payload;
      // })

      .addCase(fetchMessageBox.fulfilled, (state, action) => {
            const { data, page, search, pageSize, cacheKey, fromCache } = action.payload;

            state.messageBoxList = data || { result: [], pagination: { count: 0, no_page: 1 } };
            state.currentPage = page;
            state.pageSize = pageSize;
            state.search = search;

            const totalCount = data?.pagination?.count || 0;
            state.totalPages = Math.ceil(totalCount / pageSize);

            // ✅ Save cache
            if (!fromCache && cacheKey) {
              state.cache[cacheKey] = data;
            }

            state.status = "succeeded";
          })

      .addCase(fetchMessageBox.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Add Message to Box
      .addCase(addMessageBox.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(addMessageBox.fulfilled, (state) => {
        state.status = "succeeded";
          // 🔥 CLEAR CACHE AFTER UPDATE
          state.cache = {};
      })
      .addCase(addMessageBox.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Update Message Box
      .addCase(updateMessageBox.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateMessageBox.fulfilled, (state) => {
        state.status = "succeeded";
          // 🔥 CLEAR CACHE AFTER UPDATE
        state.cache = {};
      })
      .addCase(updateMessageBox.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearError ,setPage, setPageSize, setSearch  } = messageBoxSlice.actions;

export default messageBoxSlice.reducer;
